import { reconcileArrayRowIds } from 'features/libra/fieldValues/arrayRowIds'
import {
  isEmptyFieldValue,
  unwrapLlmValue,
} from 'features/libra/ontology/ontologyFieldDefaults'
import {
  FieldValue,
  FieldValues,
} from 'features/libra/ontology/ontologyFieldTypes'

function reconcileRows(previous: unknown, next: unknown): unknown {
  return Array.isArray(previous) && Array.isArray(next)
    ? reconcileArrayRowIds(previous, next)
    : next
}

function reconcileWrapper(
  previous: FieldValue | undefined,
  incoming: FieldValue
): FieldValue {
  if (!previous) return incoming
  const llmValue = incoming.llmValue
  return {
    ...incoming,
    value: reconcileRows(previous.value, incoming.value),
    ...(typeof llmValue === 'object' &&
      llmValue !== null &&
      'value' in llmValue && {
        llmValue: {
          ...llmValue,
          value: reconcileRows(
            unwrapLlmValue(previous.llmValue),
            llmValue.value
          ),
        },
      }),
  }
}

/**
 * Applies an AI run's field values onto the local values. A "not found"
 * result (null or empty) never wipes a value the field already has: the
 * previous wrapper and its origin are kept, and only the AI lineage and the
 * not-found flag are attached. Array rows keep their previous `_id`s where the
 * row is unchanged.
 */
export function applyAiFieldValues(
  previous: FieldValues,
  aiValues: FieldValues
): FieldValues {
  const applied: FieldValues = { ...previous }
  for (const [fieldId, incoming] of Object.entries(aiValues)) {
    const existing = previous[fieldId]
    if (
      existing &&
      isEmptyFieldValue(incoming.value) &&
      !isEmptyFieldValue(existing.value)
    ) {
      applied[fieldId] = {
        ...existing,
        ...(incoming.llmValue !== undefined && { llmValue: incoming.llmValue }),
        aiExtracted: false,
        ...(incoming.documentsSearched && {
          documentsSearched: incoming.documentsSearched,
        }),
      }
      continue
    }
    applied[fieldId] = reconcileWrapper(existing, incoming)
  }
  return applied
}
